import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDiscountList, deleteDiscount, updateDiscount } from "../../../redux/reducers/discountSlice";
import {
  Card, Table, Badge, Form, Row, Col, Button,
  InputGroup, Pagination, Modal
} from "react-bootstrap";
import {
  LuSearch, LuTag, LuChevronLeft, LuChevronRight,
  LuCircleDot, LuTrash2, LuPercent, LuDollarSign
} from "react-icons/lu";
import AddDiscount from "./AddDiscount";
import moment from "moment";

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 15, 20];

export default function DiscountList() {
  const dispatch = useDispatch();
  const selectedBusiness = useSelector((state) => state.business.selectedBusiness);
  const businessId = selectedBusiness?._id;
  const discountdata = useSelector((state) => state.discount.discountList);

  useEffect(() => {
    if (businessId) dispatch(getDiscountList(businessId));
  }, [dispatch, businessId]);

  // --- FILTER STATE ---
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // --- PAGINATION STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // --- DELETE STATE ---
  const [deletemodalshow, setDeletemodalshow] = useState(false);
  const [deleteid, setDeleteid] = useState(null);

  // --- DERIVED: filtered list ---
  const filteredDiscounts = useMemo(() => {
    if (!discountdata) return [];
    return discountdata.filter((d) => {
      const matchesSearch = d.name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || d.status === statusFilter;
      const matchesType = typeFilter === "all" || d.type === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [discountdata, searchQuery, statusFilter, typeFilter]);

  // --- PAGINATION ---
  const totalPages = Math.max(1, Math.ceil(filteredDiscounts.length / limit));
  const paginatedDiscounts = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return filteredDiscounts.slice(start, start + limit);
  }, [filteredDiscounts, currentPage, limit]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter, typeFilter, limit]);

  const hasActiveFilters = searchQuery || statusFilter !== "all" || typeFilter !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setTypeFilter("all");
  };

  // --- TOGGLE STATUS ---
  const handleToggleStatus = (discount) => {
    const newStatus = discount.status === "active" ? "inactive" : "active";
    dispatch(updateDiscount({ id: discount._id, status: newStatus }));
  };

  // --- DELETE ---
  const handleDelete = () => {
    dispatch(deleteDiscount(deleteid)).then(() => setDeletemodalshow(false));
  };

  const getTypeBadge = (type) => {
    if (type === "percentage") {
      return (
        <Badge bg="info-subtle" className="text-info rounded-pill px-3 d-inline-flex align-items-center gap-1">
          <LuPercent size={11} /> Percentage
        </Badge>
      );
    }
    return (
      <Badge bg="primary-subtle" className="text-primary rounded-pill px-3 d-inline-flex align-items-center gap-1">
        <LuDollarSign size={11} /> Fixed
      </Badge>
    );
  };

  const formatValue = (discount) =>
    discount.type === "percentage"
      ? `${discount.value}%`
      : `$${discount.value.toLocaleString()}`;

  return (
    <div className="mt-4">

      {/* --- FILTER BAR --- */}
      <Card className="border-0 shadow-sm rounded-4 p-3 mb-4">
        <Row className="g-3 align-items-end">

          <Col lg={4} md={6}>
            <Form.Label className="small fw-bold text-muted mb-1">Search</Form.Label>
            <InputGroup className="bg-light rounded-pill overflow-hidden border-0">
              <InputGroup.Text className="bg-transparent border-0 pe-0 text-muted">
                <LuSearch size={16} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search discount..."
                className="bg-transparent border-0 shadow-none py-2 small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </Col>

          <Col lg={2} md={6}>
            <Form.Label className="small fw-bold text-muted mb-1">Type</Form.Label>
            <Form.Select
              className="bg-light border-0 rounded-pill shadow-none small fw-bold py-2"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed</option>
            </Form.Select>
          </Col>

          <Col lg={2} md={6}>
            <Form.Label className="small fw-bold text-muted mb-1">Status</Form.Label>
            <Form.Select
              className="bg-light border-0 rounded-pill shadow-none small fw-bold py-2"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Col>

          {hasActiveFilters && (
            <Col lg="auto">
              <Button
                variant="outline-secondary"
                className="rounded-pill px-3 small fw-bold py-2"
                onClick={clearFilters}
              >
                Clear
              </Button>
            </Col>
          )}

          <Col lg="auto" className="ms-auto">
            <div className="text-muted small fw-medium text-end">
              <strong>{filteredDiscounts.length}</strong> of{" "}
              <strong>{discountdata?.length ?? 0}</strong> discounts
            </div>
          </Col>

        </Row>
      </Card>

      {/* --- TABLE --- */}
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle mb-0">
            <thead className="bg-light text-muted small text-uppercase">
              <tr>
                <th className="ps-4 py-3 fw-semibold">Discount</th>
                <th className="fw-semibold">Type</th>
                <th className="fw-semibold">Value</th>
                <th className="fw-semibold">Status</th>
                <th className="fw-semibold">Created</th>
                <th className="pe-4 text-end fw-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDiscounts.length > 0 ? (
                paginatedDiscounts.map((discount) => (
                  <tr key={discount._id}>

                    {/* Discount name */}
                    <td className="ps-4 py-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className="p-2 bg-light rounded-3 text-muted">
                          <LuTag size={18} />
                        </div>
                        <div>
                          <div className="fw-bold small">{discount?.name}</div>
                          <div className="text-muted" style={{ fontSize: "11px" }}>
                            ID: #{discount?._id?.slice(-6).toUpperCase()}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Type badge */}
                    <td>{getTypeBadge(discount.type)}</td>

                    {/* Value */}
                    <td>
                      <Badge
                        bg="light"
                        className="text-dark border rounded-pill fw-semibold px-2 py-1 small"
                      >
                        {formatValue(discount)}
                      </Badge>
                    </td>

                    {/* Status toggle */}
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="switch"
                          id={`discount-switch-${discount._id}`}
                          checked={discount.status === "active"}
                          onChange={() => handleToggleStatus(discount)}
                          className="mb-0"
                        />
                        <span
                          className={`small fw-semibold ${
                            discount.status === "active" ? "text-success" : "text-danger"
                          }`}
                        >
                          {discount.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </td>

                    {/* Created date */}
                    <td>
                      <div className="small fw-semibold">
                        {moment(discount.createdAt).format("MMM DD, YYYY")}
                      </div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>
                        {moment(discount.createdAt).fromNow()}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end align-items-center gap-2">
                        <AddDiscount type="update" data={discount} />
                        <Button
                          variant="light"
                          size="sm"
                          className="rounded-circle p-2"
                          title="Delete discount"
                          onClick={() => {
                            setDeleteid(discount._id);
                            setDeletemodalshow(true);
                          }}
                        >
                          <LuTrash2 size={15} className="text-danger" />
                        </Button>
                      </div>
                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-5 text-muted">
                    <LuCircleDot size={40} className="mb-2 opacity-25" />
                    <p className="mb-0 small">
                      {hasActiveFilters
                        ? "No discounts match your filters."
                        : "No discounts found."}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>

        {/* --- PAGINATION FOOTER --- */}
        {filteredDiscounts.length > 0 && (
          <Card.Footer className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted fw-medium">Rows per page:</small>
                <Form.Select
                  size="sm"
                  className="border-0 bg-light rounded-3 fw-bold py-1 px-2"
                  style={{ width: "70px", fontSize: "0.8rem" }}
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </Form.Select>
              </div>
              <small className="text-muted fw-medium border-start ps-3">
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                &nbsp;({filteredDiscounts.length} total)
              </small>
            </div>

            <Pagination
              className="mb-0 gap-1"
              style={{
                "--bs-pagination-active-bg": "#28B0C4",
                "--bs-pagination-active-border-color": "#28B0C4",
              }}
            >
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="border-0 shadow-none"
              >
                <LuChevronLeft />
              </Pagination.Prev>
              {[...Array(totalPages)].map((_, idx) => (
                <Pagination.Item
                  key={idx + 1}
                  active={idx + 1 === currentPage}
                  onClick={() => setCurrentPage(idx + 1)}
                >
                  {idx + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="border-0 shadow-none"
              >
                <LuChevronRight />
              </Pagination.Next>
            </Pagination>
          </Card.Footer>
        )}
      </Card>

      {/* --- DELETE MODAL --- */}
      <Modal
        show={deletemodalshow}
        onHide={() => setDeletemodalshow(false)}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header>
          <div className="popClose-btn" onClick={() => setDeletemodalshow(false)}>
            <i className="fas fa-times"></i>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="delete-block">
            <p>Are you sure you want to delete this discount? This action cannot be undone.</p>
          </div>
          <div className="footer-modal">
            <Button onClick={() => setDeletemodalshow(false)} className="cancel-btn">
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} className="delete-btn">
              Delete
            </Button>
          </div>
        </Modal.Body>
      </Modal>

    </div>
  );
}